import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { AcademicYear, ClassYearConfig, Student } from '../types';
import { 
  DEFAULT_ACADEMIC_YEARS, 
  getAcademicYears, 
  getClassYearConfigs, 
  saveClassYearConfig, 
  getStudentClassForYear, 
  isStudentEnrolledInYear,
  getAcademicYearRange
} from '../services/academicYearService';
import { safeStorage } from '../services/safeStorage';

interface AcademicYearContextType {
  selectedYear: string;
  setSelectedYear: (year: string) => void;
  academicYears: AcademicYear[];
  classConfigs: Record<string, ClassYearConfig>;
  loading: boolean;
  refreshClassConfigs: () => Promise<void>;
  updateClassConfig: (classId: string, className: string, config: Partial<ClassYearConfig>) => Promise<void>;
  getStudentClass: (student: Student) => { classId: string; className: string } | null;
  isStudentEnrolled: (student: Student) => boolean;
  currentYearObj: AcademicYear;
}

const AcademicYearContext = createContext<AcademicYearContextType | undefined>(undefined);

export const AcademicYearProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize with stored year or default to '2026-2027' as requested by the user for the new school year
  const [selectedYear, setSelectedYearState] = useState<string>(() => {
    return safeStorage.getItem('selected_academic_year') || '2026-2027';
  });

  const [academicYears, setAcademicYears] = useState<AcademicYear[]>(DEFAULT_ACADEMIC_YEARS);
  const [classConfigs, setClassConfigs] = useState<Record<string, ClassYearConfig>>({});
  const [loading, setLoading] = useState<boolean>(true);

  // Load available academic years
  useEffect(() => {
    getAcademicYears().then(years => {
      setAcademicYears(years);
    }).catch(err => {
      console.warn("Could not load academic years:", err);
    });
  }, []);

  // Fetch class configurations for the selected year
  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const configs = await getClassYearConfigs(selectedYear);
      setClassConfigs(configs);
    } catch (err) {
      console.error(`Error loading class configs for year ${selectedYear}:`, err);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const setSelectedYear = useCallback((year: string) => {
    setSelectedYearState(year);
    safeStorage.setItem('selected_academic_year', year);
  }, []);

  const updateClassConfig = useCallback(async (
    classId: string, 
    className: string, 
    config: Partial<ClassYearConfig>
  ) => {
    await saveClassYearConfig(selectedYear, classId, className, config);
    await fetchConfigs();
  }, [selectedYear, fetchConfigs]);

  const getStudentClass = useCallback((student: Student) => {
    return getStudentClassForYear(student, selectedYear);
  }, [selectedYear]);

  const isStudentEnrolled = useCallback((student: Student) => {
    return isStudentEnrolledInYear(student, selectedYear);
  }, [selectedYear]);

  const currentYearObj = useMemo(() => {
    const found = academicYears.find(y => y.id === selectedYear);
    const range = getAcademicYearRange(selectedYear);
    return {
      id: selectedYear,
      name: found?.name || (selectedYear === '2026-2027' ? 'תשפ״ז (2026-2027)' : `שנת ${selectedYear}`),
      startDate: found?.startDate || range.startDate,
      endDate: found?.endDate || range.endDate,
      isDefault: found?.isDefault ?? (selectedYear === '2026-2027')
    };
  }, [academicYears, selectedYear]);

  const value = useMemo(() => ({
    selectedYear,
    setSelectedYear,
    academicYears,
    classConfigs,
    loading,
    refreshClassConfigs: fetchConfigs,
    updateClassConfig,
    getStudentClass,
    isStudentEnrolled,
    currentYearObj
  }), [
    selectedYear, 
    setSelectedYear, 
    academicYears, 
    classConfigs, 
    loading, 
    fetchConfigs, 
    updateClassConfig, 
    getStudentClass, 
    isStudentEnrolled, 
    currentYearObj
  ]);

  return (
    <AcademicYearContext.Provider value={value}>
      {children}
    </AcademicYearContext.Provider>
  );
};

export const useAcademicYear = (): AcademicYearContextType => {
  const context = useContext(AcademicYearContext);
  if (!context) {
    throw new Error('useAcademicYear must be used within an AcademicYearProvider');
  }
  return context;
};
